import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./i18n";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { Layout } from "./components/layout/Layout";

// Pages
import Home from "./pages/Home";
import ComingSoon from "./pages/ComingSoon";
import Rental from "./pages/Rental";
import RentalDetail from "./pages/RentalDetail";
import Sales from "./pages/Sales";
import Parts from "./pages/Parts";
import Software from "./pages/Software";
import Services from "./pages/Services";
import Realizations from "./pages/Realizations";
import Contact from "./pages/Contact";
import Quote from "./pages/Quote";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Account from "./pages/Account";
import AuthCallback from "./pages/AuthCallback";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminFinance from "./pages/admin/Finance";
import AdminProducts from "./pages/admin/Products";
import AdminOrders from "./pages/admin/Orders";
import AdminRentals from "./pages/admin/Rentals";
import AdminMessages from "./pages/admin/Messages";
import AdminQuoteRequests from "./pages/admin/QuoteRequests";
import AdminMenuManagement from "./pages/admin/MenuManagement";

// 🚧 MAINTENANCE MODE - Set to true to show Coming Soon page
const MAINTENANCE_MODE = true;

// Router component to handle OAuth callback
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // If maintenance mode is ON, show Coming Soon page for public routes
  // Admin routes still accessible
  if (MAINTENANCE_MODE) {
    return (
      <Routes>
        {/* Coming Soon page for public */}
        <Route path="/" element={<ComingSoon />} />
        
        {/* Auth routes still work for admin access */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        
        {/* Admin Routes remain accessible */}
        <Route path="/admin" element={<Layout><AdminDashboard /></Layout>} />
        <Route path="/admin/finance" element={<Layout><AdminFinance /></Layout>} />
        <Route path="/admin/products" element={<Layout><AdminProducts /></Layout>} />
        <Route path="/admin/orders" element={<Layout><AdminOrders /></Layout>} />
        <Route path="/admin/rentals" element={<Layout><AdminRentals /></Layout>} />
        <Route path="/admin/messages" element={<Layout><AdminMessages /></Layout>} />
        <Route path="/admin/quote-requests" element={<Layout><AdminQuoteRequests /></Layout>} />
        <Route path="/admin/menu" element={<Layout><AdminMenuManagement /></Layout>} />
        
        {/* Account for signed-in users */}
        <Route path="/account" element={<Layout><Account /></Layout>} />
        
        {/* Redirect all other routes to Coming Soon */}
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    );
  }
  
  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/rental" element={<Rental />} />
        <Route path="/rental/:productId" element={<RentalDetail />} />
        <Route path="/sales" element={<Sales />} />
        <Route path="/parts" element={<Parts />} />
        <Route path="/software" element={<Software />} />
        <Route path="/services" element={<Services />} />
        <Route path="/realizations" element={<Realizations />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/soumission" element={<Quote />} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Cart & Checkout */}
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        
        {/* Account */}
        <Route path="/account" element={<Account />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/finance" element={<AdminFinance />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/rentals" element={<AdminRentals />} />
        <Route path="/admin/messages" element={<AdminMessages />} />
        <Route path="/admin/quote-requests" element={<AdminQuoteRequests />} />
        <Route path="/admin/menu" element={<AdminMenuManagement />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
